// @flow

import * as Sentry from '@sentry/react';
import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import analytics from '../analytics';
import {useUser} from '../modules/auth/utils';
import {useStoreInfo} from '../modules/store/utils';
import {useOnboardingInformation} from '../modules/on-boarding/utils/hooks';
import {getBasicDomain} from '../utils/component';
import type {RootState} from '../modules';

const countrySelector = (state: RootState) => state.country.country;
const isGettingStoreCountrySelector = (state: RootState) =>
  state.country.isGettingStoreCountry;
const contactsSelector = (state: RootState) => state.information.contact;

export function useAnalyticsTracking() {
  const user = useUser();
  const store = useStoreInfo();
  const country = useSelector(countrySelector);
  const isGettingStoreCountry = useSelector(isGettingStoreCountrySelector);
  const contacts = useSelector(contactsSelector);

  const {goalInformation, supportInformation} = useOnboardingInformation();

  const storeDomain = useMemo(() => {
    if (store?.domains) return getBasicDomain(store.domains);
  }, [store]);

  const phoneNumber = contacts?.find(c => c.contactType.name === 'PHONE_NUMBER')
    ?.data;
  const whatsappNumber = contacts?.find(
    c => c.contactType.name === 'WHATS_APP_NUMBER'
  )?.data;

  const storeId = store && store.id;
  const userId = user && user.id;

  // Initialize event tracking (only once per session)
  useEffect(() => {
    if (storeId && userId) {
      analytics.initializeEventTracking(storeId, userId);
    }
  }, [storeId, userId]);

  // Identify user for analytics
  useEffect(() => {
    if (
      !phoneNumber ||
      !user ||
      !store ||
      !storeDomain ||
      isGettingStoreCountry
    )
      return;

    const store_configure_token = user?.authnTokens?.find(
      t => t.enabled && t.authnTokenType === 'SIGN_UP'
    );

    analytics.identify(user.id, {
      contact_phone: phoneNumber,
      whatsapp_number: whatsappNumber,
      phone: user.phone,
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      name: `${user.firstName} ${user.lastName}`,
      store_id: store.id,
      store_domain: storeDomain,
      store_category: store.storeType?.label,
      store_country: country?.name,
      store_tier: store.storeTier?.name,
      store_name: store.name,
      store_configure_token: store_configure_token
        ? encodeURIComponent(store_configure_token.token)
        : undefined,
      referral_id: store.referrerId,
      referral_code: store.referrerSlug,
      reseller_user_id: store.resellerUserId,
      reseller_slug: store.resellerSlug,
      creation_date: store.createdAt,
      onboarding_type_id: store.onboardingTypeId,
      get_assistance: supportInformation?.getAssistance,
      store_goal: goalInformation?.storeGoal,
      sales_frequency: goalInformation?.salesFrequency,
      store_interests: goalInformation?.storeInterests?.join(', '),
    });

    Sentry.setUser({
      id: user.id,
      email: user.email,
      phone: user.phone,
      name: `${user.firstName} ${user.lastName}`,
    });
  }, [
    user,
    store,
    storeDomain,
    country,
    isGettingStoreCountry,
    goalInformation,
    supportInformation,
    phoneNumber,
    whatsappNumber,
  ]);

  // Fire session started event
  useEffect(() => {
    if (user) {
      analytics.fireCustomEvent('User Session Started', undefined, [
        'user-guiding',
      ]);
    }
  }, [user]);
}
